import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InfoIcon from '@mui/icons-material/Info';
import type { InventoryItem } from '../types/inventory.types';
import { getCurrencySymbol, getTaxStatusText } from './formatters';

/**
 * Tablo hücre değerlerini render eden utility fonksiyonları
 */

/**
 * Tablo hücresinin değerini elde et ve render et
 */
export const getCellValue = (
  item: InventoryItem,
  columnId: string,
  options: {
    getCategoryName: (id: string | null) => string;
    getBrandName: (id: string | null) => string;
    getBranchName: (id: string | null) => string;
    getWarehouseName: (id: string | null) => string;
    localizeUnit?: (unitValue?: string | null) => string;
    ecommerceStatuses: { [key: string]: boolean };
    onCellClick?: (item: InventoryItem, field: 'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name') => void;
    onMenuOpen?: (event: React.MouseEvent<HTMLElement>, itemId: string) => void;
    onOpenImageLibraryModal?: (item: InventoryItem) => void;
    rowIndex?: number;
    page?: number;
    rowsPerPage?: number;
  }
): React.ReactNode => {
  const {
    getCategoryName,
    getBrandName,
    getBranchName,
    getWarehouseName,
    localizeUnit,
    ecommerceStatuses,
    onCellClick,
    onMenuOpen,
    onOpenImageLibraryModal,
    rowIndex,
    page,
    rowsPerPage
  } = options;

  switch (columnId) {
    case 'rowNumber':
      // Sayfalama ile birlikte sıra numarasını hesapla
      if (rowIndex !== undefined && page !== undefined && rowsPerPage !== undefined) {
        return (page * rowsPerPage) + rowIndex + 1;
      }
      return '-';
    case 'name':
      return (
        <Box
          onClick={() => onCellClick?.(item, 'name')}
          sx={{ 
            cursor: 'pointer',
            color: 'text.primary',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' } 
          }}
        >
          {item.name}
        </Box>
      );
    case 'barcode':
      return item.barcode || '-';
    case 'scaleCode':
      return item.scaleCode || '-';
    case 'stockCode':
      return item.stockCode;
    case 'category':
      return getCategoryName(item.categoryId || null);
    case 'brand':
      return getBrandName(item.brandId || null);
    case 'branch':
      return getBranchName(item.branchesId || null);
    case 'quantity':
      // ⚠️ TODO: Hierarchical view'de toplam quantity gösterilecek
      // Şimdilik warehouseStocks varsa toplam, yoksa 0
      const totalQuantity = item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0;
      return (
        <Box
          onClick={() => onCellClick?.(item, 'quantity')}
          sx={{ 
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' } 
          }}
        >
          {totalQuantity.toLocaleString('tr-TR')}
        </Box>
      );
    case 'unit':
      return (
        <Box
          sx={{ 
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%'
          }}
        >
          {localizeUnit ? localizeUnit(item.unit) : item.unit}
        </Box>
      );
    case 'purchasePrice':
      return `${item.purchasePrice.toLocaleString('tr-TR')} ${getCurrencySymbol(item.purchaseCurrency || 'TRY')}`;
    case 'salePrice':
      // Banka bakiyelerindeki görünüme (Typography variant="body2", bold, success renk)
      // birebir uyacak şekilde düzenlendi. Yapıyı bozmadan sadece sunum değiştirildi.
      return (
        <Typography
          onClick={() => onCellClick?.(item, 'salePrice')}
          variant="body2"
          fontWeight="bold"
          color="success.main"
          sx={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' }
          }}
        >
          {item.salePrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(item.saleCurrency || 'TRY')}
        </Typography>
      );
    case 'vatRate':
      return (
        <Box
          onClick={() => onCellClick?.(item, 'vatRate')}
          sx={{ 
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' } 
          }}
        >
          %{item.vatRate || 0}
        </Box>
      );
    case 'vatStatus':
      return getTaxStatusText(item.vatStatus || 'excluded');
    case 'sctRate':
      return item.sctRate ? `%${item.sctRate}` : '-';
    case 'sctStatus':
      return item.sctRate ? getTaxStatusText(item.sctStatus || 'excluded') : '-';
    case 'warehouse':
      // ⚠️ TODO: Hierarchical view'de alt satırlarda depo isimleri gösterilecek
      // Şimdilik tüm depoların listesi veya '-'
      if (item.warehouseStocks && item.warehouseStocks.length > 0) {
        return item.warehouseStocks.map(ws => ws.warehouseName || ws.warehouseId).join(', ');
      }
      return '-';
    case 'location':
      if (item.warehouseStocks && item.warehouseStocks.length > 0) {
        const withLoc = item.warehouseStocks.filter((ws: any) => ws.locationCode);
        if (withLoc.length > 0) return withLoc.map((ws: any) => ws.locationCode).join(', ');
      }
      return '-';
    case 'description':
      return item.description || '-';
    case 'ecommerce':
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {ecommerceStatuses[item.id] === true ? (
            <CheckIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
          ) : (
            <RemoveIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
          )}
        </Box>
      );
    case 'qrmenu':
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {(item as any).isQRMenu === true ? (
            <CheckIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
          ) : (
            <RemoveIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
          )}
        </Box>
      );
    case 'departmentNo':
      // ÖKC - Departman Numarası (tıklanabilir)
      return (
        <Box
          onClick={() => onCellClick?.(item, 'departmentNo')}
          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
        >
          {item.departmentNo !== null && item.departmentNo !== undefined ? item.departmentNo : '-'}
        </Box>
      );
    case 'canBeNegative':
      // ✅ YENİ: Stok Eksiye Düşebilir Mi? (Eksi sütunu)
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {item.canBeNegative === true ? (
            <CheckIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
          ) : (
            <RemoveIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
          )}
        </Box>
      );
    case 'actions':
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          {/* Stok Ayrıntıları Butonu */}
          <Tooltip title="Stok Ayrıntıları">
            <IconButton
              size="small"
              onClick={() => onCellClick?.(item, 'details' as any)}
              sx={{ color: 'primary.main' }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* KALICI KURAL: Child urun kontrolu parentItemId ile yapilir. */}
          {item.parentItemId ? (
            // Alt ürün için pasif menü butonu (tooltip ile)
            <Tooltip title="Ana ürünü düzenleyiniz">
              <span>
                <IconButton size="small" disabled={true}>
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            // Ana ürün için normal menü butonu
            <IconButton
              size="small"
              onClick={(e) => onMenuOpen?.(e, item.id)}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      );
    default:
      return '-';
  }
};

/**
 * Resim hücresini render et
 * @param item - Stok ürünü
 * @param onImageClick - Resim tıklandığında çağrılacak callback (item, event, hasImage)
 */
export const renderImageCell = (
  item: InventoryItem,
  onImageClick?: (item: InventoryItem, event: React.MouseEvent<HTMLElement>, hasImage: boolean) => void
): React.ReactNode => {
  // Resim URL'lerini parse et (virgülle ayrılmış olabilir)
  const hasImage = !!(item.imageUrl && item.imageUrl.trim() !== '');
  
  if (hasImage) {
    // İlk resmi göster (virgülle ayrılmışsa)
    const firstImageUrl = item.imageUrl!.split(',')[0].trim();
    
    return (
      <Box
        component="img"
        src={firstImageUrl}
        alt={item.name}
        sx={{
          width: '40px',
          height: '40px',
          objectFit: 'cover',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            opacity: 0.8,
            transform: 'scale(1.05)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          onImageClick?.(item, e, true);
        }}
      />
    );
  }

  // Resim yoksa - ikon göster, direkt resim seçme modalı açılacak
  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onImageClick?.(item, e, false);
      }}
      sx={{
        width: '40px',
        height: '40px',
        bgcolor: 'grey.100',
        border: '1px dashed',
        borderColor: 'grey.300',
        borderRadius: '4px',
        '&:hover': {
          bgcolor: 'grey.200',
          borderColor: 'primary.main'
        }
      }}
    >
      <PhotoCameraIcon fontSize="small" color="action" />
    </IconButton>
  );
};

